import { RadioButtonGroup } from "@heroui-pro/react/radio-button-group";
import { useReducedMotion } from "framer-motion";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { ChoiceCard, type ChoiceCardBadge } from "@funnel-ui/primitives/ChoiceCard";
import { DynamicIcon } from "@funnel-ui/primitives/DynamicIcon";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

export type SingleChoiceOption = {
  id: string;
  label: string;
  description?: string;
  imageUrl?: string;
  /** CSS `object-position` for cover images (e.g. "top", "center"). */
  imagePosition?: string;
  imageAspect?: "square" | "portrait" | "landscape";
  iconLeading?: string;
  badge?: ChoiceCardBadge;
  disabled?: boolean;
};

type SingleChoiceProps = {
  question: string;
  description?: string;
  /** Small uppercase label rendered above the question in accent color. */
  eyebrow?: string;
  options: SingleChoiceOption[];
  initialValue?: string;
  layout?: "row" | "tile" | "cover";
  columns?: 1 | 2;
  /** When false, hides the radio indicator on row layout. Defaults to true. */
  showIndicator?: boolean;
  autoAdvance?: boolean;
  onAnswer: (optionId: string) => void;
  className?: string;
  testId?: string;
};

const AUTO_ADVANCE_DELAY_MS = 220;
const AUTO_ADVANCE_DELAY_REDUCED_MS = 40;

const SingleChoiceImpl = ({
  question,
  description,
  eyebrow,
  options,
  initialValue,
  layout,
  columns = 1,
  showIndicator = true,
  autoAdvance = true,
  onAnswer,
  className,
  testId,
}: SingleChoiceProps) => {
  const prefersReduced = useReducedMotion();
  const [value, setValue] = useState<string | undefined>(initialValue);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const handleChange = (next: string): void => {
    setValue(next);
    if (!autoAdvance) {
      onAnswer(next);
      return;
    }
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(
      () => onAnswer(next),
      prefersReduced ? AUTO_ADVANCE_DELAY_REDUCED_MS : AUTO_ADVANCE_DELAY_MS,
    );
  };

  // Re-selecting the already-chosen option doesn't fire RadioButtonGroup's
  // onChange (value didn't change). Intercept clicks on the selected item so
  // the user can tap the same choice again to advance.
  const valueRef = useRef(value);
  valueRef.current = value;
  const handleReselect = useCallback(
    (e: React.MouseEvent): void => {
      if (!autoAdvance || valueRef.current === undefined) return;
      const target = e.target as HTMLElement;
      // React Aria adds `data-selected` on the selected radio item.
      if (target.closest("[data-selected]")) {
        if (advanceTimer.current) clearTimeout(advanceTimer.current);
        advanceTimer.current = setTimeout(
          () => onAnswer(valueRef.current!),
          prefersReduced ? AUTO_ADVANCE_DELAY_REDUCED_MS : AUTO_ADVANCE_DELAY_MS,
        );
      }
    },
    [autoAdvance, onAnswer, prefersReduced],
  );

  const gridCols = columns === 2 ? "grid grid-cols-2 gap-3" : "flex flex-col gap-3";

  return (
    <section data-testid={testId} className={`flex flex-col gap-6 ${className ?? ""}`}>
      <StepHeading
        title={question}
        description={description}
        eyebrow={eyebrow}
        editablePath="question"
      />
      <div onClick={handleReselect}>
        <RadioButtonGroup
          aria-label={question}
          className={gridCols}
          value={value ?? null}
          onChange={handleChange}
          name={`single-choice-${testId ?? "default"}`}
          variant="secondary"
          layout={columns === 2 ? "grid" : "flex"}
        >
          {options.map((option, index) => (
            <ChoiceCard
              key={option.id}
              role="radio"
              value={option.id}
              label={option.label}
              description={option.description}
              imageUrl={option.imageUrl}
              imagePosition={option.imagePosition}
              imageAspect={option.imageAspect}
              iconLeading={
                option.iconLeading && <DynamicIcon icon={option.iconLeading} size={20} />
              }
              badge={option.badge}
              layout={layout === "cover" ? "cover" : (layout ?? (columns >= 2 ? "tile" : "row"))}
              disabled={option.disabled}
              showIndicator={showIndicator}
              optionIndex={index}
            />
          ))}
        </RadioButtonGroup>
      </div>
    </section>
  );
};

export const SingleChoice = memo(SingleChoiceImpl);

export default SingleChoice;
